import React, { memo, SetStateAction, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Box as FormRow } from '@node-space/storybook-components/dist/Box'
import { StatusCard } from '@node-space/storybook-components/dist/StatusCard'
import { TextAction } from '@node-space/storybook-components/dist/TextAction'
import { Illustration, Input, Link, Text } from '@node-space/ui-components'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults'
import { ActionListSkeletonLoader } from 'components/Skeletons/ActionListSkeletonLoader'
import { Description } from 'components/text/Description'
import { WALLETS_NUMBER_OF_ROWS } from 'constants/General'
import { useWalletControllerContext } from 'hooks/useWalletControllerContext'
import { WalletType } from 'types/types'
import { TWalletType, WalletTypes } from 'types/wallets'
import { WalletAction } from '../Modal/WalletModal'
import { WalletSheet } from '../Sheet/WalletSheet'
import WalletList from './WalletList'

interface WalletTableProps {
  testid: string
  wallets: WalletType[]
  walletType: TWalletType
  setWalletModalAction: React.Dispatch<SetStateAction<WalletAction>>
  searchQuery?: string
  setShowWalletModal?: React.Dispatch<SetStateAction<boolean>>
  clearFilters?: () => void
  onSearchTermChange?: React.Dispatch<SetStateAction<string>>
  isWalletBeingSearched?: boolean
  debouncedSearch?: string
}

const WalletTable = ({
  searchQuery,
  onSearchTermChange,
  wallets,
  walletType,
  setShowWalletModal,
  clearFilters,
  setWalletModalAction,
  isWalletBeingSearched,
  debouncedSearch,
}: WalletTableProps) => {
  const { t } = useTranslation()
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  const { isFetching, isSuccess, hasErrors } = useWalletControllerContext()

  if (hasErrors) {
    return (
      <StatusCard
        customIcon={<Illustration name="CloudLightning" color="error" />}
        headline={t('requestErrors.pageErrorIntro')}
        borderless
      >
        <Description>
          {`${t('requestErrors.comeBackLater')} `}
          <TextAction isWindowReload text={t('requestErrors.refreshPage')} />.
        </Description>
      </StatusCard>
    )
  }

  return (
    <>
      <Box paddingY={20} flex>
        <FormRow width="full">
          <Input
            searchable
            testId="filter-search-field"
            placeholderText={t('walletsPage.searchPlaceholder')}
            onChange={event => onSearchTermChange?.(event.target.value)}
            value={searchQuery || ''}
            {...(!!!wallets?.length && !!!searchQuery?.length && { disabled: true })}
          />
        </FormRow>
      </Box>

      {!!!wallets?.length && isWalletBeingSearched && !isFetching ? (
        <NoSearchResults clearSearchCriteria={clearFilters} />
      ) : isSuccess && !!!wallets?.length && !isFetching && !debouncedSearch ? (
        <StatusCard
          headline={t('wallets.emptyStateForWalletsHeadline')}
          testid="no-wallets-content"
        >
          {walletType === WalletTypes.FIAT ? (
            <Text variant="body-label" color="prominent">
              {t('createFiatWalletDisclaimer')}
              <Link href="https://help.bvnk.com/hc/en-us/articles/13995679766930">
                {` ${t('learnMore')}`}
              </Link>
            </Text>
          ) : (
            <Text variant="body-label">{t('wallets.emptyStateForWalletsDescription')}</Text>
          )}
        </StatusCard>
      ) : isFetching ? (
        <ActionListSkeletonLoader hasPopoverMenu rows={WALLETS_NUMBER_OF_ROWS} />
      ) : (
        <WalletList
          setIsSheetOpen={setIsSheetOpen}
          wallets={wallets}
          walletType={walletType}
          setWalletModalAction={setWalletModalAction}
          setShowWalletModal={setShowWalletModal}
        />
      )}
      <WalletSheet isSheetOpen={isSheetOpen} />
    </>
  )
}

export default memo(WalletTable)
